var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { AbstractChartContent } from './abstract-chart-content';
import { ReplaySubject } from 'rxjs';
import { isMobile } from './utils/browser';
import { getLocale, mapTZ2ChartIQTZ } from './utils/parse';
import * as moment from 'moment-timezone';
import html2canvas from 'html2canvas';
/**
 * Implementation of abstract chart content based on iframes with the chart iq advanced UI
 *
 * TODO change custom events with post messages
 *
 * @class ChartContent
 */
var ChartContent = /** @class */ (function (_super) {
    __extends(ChartContent, _super);
    /**
     * @constructor
     * @inheritDoc
     */
    function ChartContent(containerDiv, config) {
        var _this = _super.call(this, containerDiv, config) || this;
        _this.chartContentNature = 'full';
        _this._currentResults = null;
        _this.themeChangedSub = new ReplaySubject();
        _this.iframe = containerDiv;
        if (!_this.iframe) {
            console.error(_this.iframe);
            throw Error('Chart iframe container not valid!');
        }
        return _this;
    }
    /**
     *
     * @inheritDoc
     */
    ChartContent.prototype.init = function (options) {
        var _this = this;
        if (!this._dataHandler) {
            throw new Error('no data handler attached!');
        }
        this.options = options;
        if (this.options.idNotation) {
            this._idNotation = this.options.idNotation;
        }
        if (this.options.priceType) {
            this._priceType = this.options.priceType;
        }
        if (this.options.uniqueId) {
            this.uniqueId = this.options.uniqueId;
            this.chartSettingsKey = 'myChartLayout' + this.uniqueId;
        }
        if (this.options.defaultChartType) {
            this.defaultChartType = this.options.defaultChartType;
        }
        this.readySub.subscribe(function (stxx) {
            stxx.chart.xAxis.formatter = function (labelDate, gridType, timeUnit, timeUnitMultiplier, defaultText) {
                if (timeUnit === 86400000) {
                    return _this._dataHandler.formatDateTime(labelDate.getTime(), false);
                }
                else if (timeUnit < 86400000) {
                    return _this._dataHandler.formatDateTime(labelDate.getTime(), true);
                }
                return defaultText;
            };
            stxx.chart.yAxis.priceFormatter = function (stx, panel, price) {
                return _this._dataHandler.formatValue(price, { idNotation: _this._idNotation });
            };
        });
        if (this.iframe && this.iframe.contentWindow) {
            this._CIQ = this.iframe.contentWindow.CIQ;
            this._iframeContentWindow = this.iframe.contentWindow;
            this._currentResults = null;
            return this.start();
        }
        return Promise.reject('It is not possible to initialize chart');
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype.getPNG = function () {
        if (this._iframeContentWindow) {
            var element = this._iframeContentWindow.document.getElementsByClassName('ciq-chart-area')[0];
            return html2canvas(element).then(function (canvas) {
                return canvas.toDataURL('image/png');
            });
        }
        return Promise.resolve(null);
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype.getCanvas = function () {
        if (this._iframeContentWindow) {
            var element = this._iframeContentWindow.document.getElementsByClassName('ciq-chart-area')[0];
            return html2canvas(element).then(function (canvas) {
                return canvas;
            });
        }
        return Promise.resolve(null);
    };
    /**
     * Destroy subscriptions for prices, volumes and benchmarks
     *
     * @method destroy
     */
    ChartContent.prototype.destroy = function () {
        this._removeChartInformation();
        this._iframeContentWindow = null;
        this._CIQ = null;
        _super.prototype.destroy.call(this);
    };
    /**
     * @param {boolean?} persist
     */
    ChartContent.prototype.clearContent = function (persist) {
        if (persist === void 0) { persist = false; }
        if (persist) {
            this._removeChartConfiguration();
            this._removeChartInformation();
        }
        _super.prototype.clearContent.call(this, persist);
    };
    /**
     * Shows info in the console
     */
    ChartContent.prototype.showInfoOnHover = function () {
        var _this = this;
        if (localStorage.getItem('ptDebugOnHover') === 'true') {
            console.debug('Chart options: ');
            console.debug(this._chartLayout);
            console.debug('Data: ');
            console.debug(this._currentResults);
            console.debug('Trading Schedule: ');
            console.debug(this._dataHandler.eventList);
            if (this._dataHandler.refData && Object.keys(this._dataHandler.refData).length > 0) {
                console.debug('Benchmarks Data: ');
                Object.keys(this._dataHandler.refData).forEach(function (benchmarkNotation) {
                    console.debug(_this._dataHandler.refData[benchmarkNotation]);
                });
            }
        }
    };
    /**
     * @param {any} markerTypes
     */
    ChartContent.prototype.setMarkerTypes = function (markerTypes) {
        if (this._chartContentConfig && this._iframeContentWindow) {
            this._chartContentConfig.markerTypes = markerTypes;
            var markerTypesEvent = new CustomEvent('markerTypesEvent', {
                detail: {
                    markerTypes: markerTypes,
                },
            });
            this._iframeContentWindow.dispatchEvent(markerTypesEvent);
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype.removeBenchmark = function (idNotation) {
        console.warn('Not implemented');
    };
    /**
     * Sends a message to the chart to add a benchmark for comparison
     * @method _addBenchmark
     * @param {number} idNotation
     * @param {string?} color
     * @param {string?} name
     */
    ChartContent.prototype.addBenchmark = function (idNotation, color, name) {
        var _this = this;
        if (!this._idNotation) {
            //if no initialized, just add as main instrument and initialize
            this.idNotation = idNotation;
        }
        else if (this._iframeContentWindow) {
            if (!this._dataHandler.isBenchmarkSubscribed(idNotation)) {
                this._getReferenceData(idNotation).then(function (data) {
                    var benchmarkName = idNotation;
                    var benchmarkCurrency = '';
                    var benchmarkExchange = '';
                    if (data && data['NameInstrument']) {
                        benchmarkName = data['NameInstrumentShort'];
                        if (!benchmarkExchange) {
                            benchmarkName = data['NameInstrument'];
                        }
                        benchmarkCurrency = data['NameCurrency'];
                        benchmarkExchange = data['NameExchangeShort'];
                        if (!benchmarkExchange) {
                            benchmarkExchange = data['NameExchange'];
                        }
                    }
                    var addBenchmarkEvent = new CustomEvent('ChartWidgetAddBenchmark', {
                        detail: {
                            idNotation: _this._idNotation,
                            priceType: _this._priceType,
                            withEarnings: _this._withEarnings,
                            benchmark: idNotation,
                            color: color,
                            name: name || benchmarkName,
                            currency: benchmarkCurrency,
                            exchange: benchmarkExchange,
                        },
                    });
                    //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
                    _this._iframeContentWindow.dispatchEvent(addBenchmarkEvent);
                });
            }
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype.changeTheme = function (theme) {
        if (this._iframeContentWindow) {
            var changeThemeEvent = new CustomEvent('ChartWidgetChangeTheme', {
                detail: {
                    idNotation: this._idNotation,
                    priceType: this._priceType,
                    withEarnings: this._withEarnings,
                    theme: theme,
                },
            });
            //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
            this._iframeContentWindow.dispatchEvent(changeThemeEvent);
        }
        _super.prototype.changeTheme.call(this, theme);
    };
    /**
     * Sends a message to the chart to change the timezone to be displayed
     * @method changeLanguage
     * @param {string} lang
     */
    ChartContent.prototype.changeLanguage = function (lang) {
        this.language = lang;
        if (this._iframeContentWindow) {
            var locale = getLocale('en');
            if (this.language) {
                locale = getLocale(this.language);
            }
            var changeLanguageEvent = new CustomEvent('ChartWidgetChangeLanguage', {
                detail: {
                    idNotation: this._idNotation,
                    priceType: this._priceType,
                    withEarnings: this._withEarnings,
                    language: this.language,
                    locale: locale,
                },
            });
            //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
            this._iframeContentWindow.dispatchEvent(changeLanguageEvent);
        }
    };
    /**
     * Sends a message to the chart to change the timezone to be displayed
     * @method _changeTimeZone
     * @param {string} tz
     */
    ChartContent.prototype.changeTimeZone = function (tz) {
        this.timezone = tz;
        if (this._iframeContentWindow && this._CIQ && this._CIQ.timeZoneMap) {
            var timeZone = mapTZ2ChartIQTZ(this._CIQ.timeZoneMap, this.timezone);
            if (timeZone) {
                var changeTimeZoneEvent = new CustomEvent('ChartWidgetChangeTimeZone', {
                    detail: {
                        idNotation: this._idNotation,
                        priceType: this._priceType,
                        withEarnings: this._withEarnings,
                        timeZone: timeZone,
                    },
                });
                //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
                this._iframeContentWindow.dispatchEvent(changeTimeZoneEvent);
            }
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype.setRange = function (from, to) {
        if (this._iframeContentWindow) {
            var setRangeEvent = new CustomEvent('ChartWidgetSetRange', {
                detail: {
                    from: from,
                    to: to,
                },
            });
            //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
            this._iframeContentWindow.dispatchEvent(setRangeEvent);
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype.eodFallback = function () {
        if (this._iframeContentWindow) {
            var chartWidgetEodFallbackEvent = new CustomEvent('ChartWidgetEodFallback');
            //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
            this._iframeContentWindow.dispatchEvent(chartWidgetEodFallbackEvent);
        }
    };
    /**
     * @param {any} params
     * @param {any} dataCallback
     * @private
     */
    ChartContent.prototype._announceError = function (params, dataCallback) {
        _super.prototype._announceError.call(this, params, dataCallback);
        console.error('Error while retrieving data.');
        console.error(params);
        console.error(dataCallback);
        if (this._CIQ) {
            this._CIQ.alert(dataCallback);
            if (this._chartContentConfig && this._chartContentConfig.announceError) {
                this._chartContentConfig.announceError(dataCallback);
            }
        }
    };
    /**
     *
     * @inheritDoc
     */
    ChartContent.prototype._sendChangeInformationEvent = function () {
        var _this = this;
        if (this._iframeContentWindow) {
            Object.keys(this._dataHandler.refData).forEach(function (benchmarkNotation) {
                _this._removeBenchmarkInformation(benchmarkNotation);
            });
            var changeNotationEvent = new CustomEvent('ChartWidgetChangeNotation', {
                detail: {
                    idNotation: this._idNotation,
                    priceType: this._priceType,
                    withEarnings: this._withEarnings,
                    marketFactory: this._getMarketFactory(),
                },
            });
            //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
            this._iframeContentWindow.dispatchEvent(changeNotationEvent);
            this._createPriceSubscription();
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype._sendCurrentRecordUpdateChartEvent = function (ohlc, idNotation, eventName) {
        if (!ohlc || ohlc.length !== 7) {
            return;
        }
        if (this._CIQ && this._CIQ.series && this._idNotation !== idNotation) {
            if (!this._CIQ.series[idNotation + '#' + this._priceType.toLowerCase() + '#' + this._withEarnings]) {
                this._removeBenchmarkInformation(idNotation);
                return;
            }
        }
        var volume = 0;
        if (ohlc[5]) {
            volume = ohlc[5];
        }
        //noinspection TypeScriptUnresolvedFunction
        var streamTradeEvent = new CustomEvent('ChartWidgetCurrentRecord', {
            detail: {
                benchmark: this._idNotation !== idNotation,
                idNotation: idNotation,
                priceType: this._priceType,
                withEarnings: this._withEarnings,
                ohlc: ohlc,
                volume: volume,
                dateZone: ohlc[0],
                dateZoneStr: moment.tz(ohlc[0] * 1000, this.timezone).format('YYYYMMDDHHmmssmmm'),
            },
        });
        if (this._iframeContentWindow) {
            this._iframeContentWindow.dispatchEvent(streamTradeEvent);
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype._sendUpdateChartEvent = function (update, idNotation, eventName) {
        if (this._CIQ && this._CIQ.series && this._idNotation !== idNotation) {
            if (!this._CIQ.series[idNotation + '#' + this._priceType.toLowerCase() + '#' + this._withEarnings]) {
                this._removeBenchmarkInformation(idNotation);
                return;
            }
        }
        var volume = 0;
        if (update.volume) {
            volume = update.volume;
        }
        //noinspection TypeScriptUnresolvedFunction
        var streamTradeEvent = new CustomEvent('ChartWidgetStreamTradeLast', {
            detail: {
                benchmark: this._idNotation !== idNotation,
                idNotation: idNotation,
                priceType: this._priceType,
                withEarnings: this._withEarnings,
                value: update.value,
                volume: volume,
                dateZone: update.date,
                dateZoneStr: moment.tz(update.date, this.timezone).format('YYYYMMDDHHmmssmmm'),
            },
        });
        if (this._iframeContentWindow) {
            this._iframeContentWindow.dispatchEvent(streamTradeEvent);
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype._sendErrorToChart = function (error, idNotation) {
        if (this._iframeContentWindow) {
            var errorEvent = new CustomEvent('ChartWidgetError', {
                detail: {
                    idNotation: this._idNotation,
                    priceType: this._priceType,
                    withEarnings: this._withEarnings,
                    error: error,
                },
            });
            //noinspection TypeScriptUnresolvedFunction,TypeScriptUnresolvedVariable
            this._iframeContentWindow.dispatchEvent(errorEvent);
        }
    };
    /**
     * @inheritDoc
     */
    ChartContent.prototype._initChartEngine = function () {
        var _this = this;
        this.timezone = 'Europe/London';
        if (this.options.timezone) {
            this.timezone = this.options.timezone;
        }
        if (this._CIQ && this._CIQ.timeZoneMap) {
            this.timezone = mapTZ2ChartIQTZ(this._CIQ.timeZoneMap, this.timezone);
        }
        // this.changeTimeZone(this.options.timezone);
        this.language = 'en';
        if (this.options.language) {
            this.language = this.options.language;
        }
        this.theme = 'dark';
        if (this.options.theme) {
            this.theme = this.options.theme;
        }
        if (this.options.defaultPriceRelativeInstrument) {
            this.defaultPriceRelativeInstrument = this.options.defaultPriceRelativeInstrument;
        }
        return this._getReferenceData(this.defaultPriceRelativeInstrument).then(function (data) {
            var defaultPriceRelativeInstrumentName = _this.defaultPriceRelativeInstrument + '';
            if (data && data['NameInstrumentShort']) {
                defaultPriceRelativeInstrumentName = data['NameInstrumentShort'];
            }
            //noinspection TypeScriptUnresolvedVariable
            var initEvent = new CustomEvent('ChartWidgetInitialized', {
                detail: {
                    widgetKey: _this.uniqueId || '',
                    contentConfig: _this._chartContentConfig,
                    quoteFeed: _this.getQuoteFeed(),
                    persistLayout: _this._persistLayout(),
                    persistDrawings: _this._persistDrawings(),
                    removeSeries: _this._removeSeries(),
                    marketFactory: _this._getMarketFactory(),
                    formatTransform: _this._formatTransform(),
                    formatTransformDate: _this._formatTransformDate(),
                    translate: _this._translate(),
                    idNotation: _this._idNotation,
                    priceType: _this._priceType,
                    withEarnings: _this._withEarnings,
                    language: _this.language,
                    locale: getLocale(_this.language),
                    timeZone: _this.timezone,
                    theme: _this.theme,
                    themeChanged: function (theme) { return _this.themeChangedSub.next(theme); },
                    readySub: _this.readySub,
                    brandingPrefix: _this.options.brandingPrefix || 'fdsg',
                    disabledEvents: _this.disabledEvents,
                    pushData: _this.pushData,
                    isMobile: isMobile(),
                    defaultPriceRelativeInstrument: _this.defaultPriceRelativeInstrument,
                    defaultPriceRelativeInstrumentName: defaultPriceRelativeInstrumentName,
                    defaultChartType: _this.defaultChartType,
                    getMarkers: _this._getMarkers(),
                },
            });
            if (_this._iframeContentWindow) {
                _this._iframeContentWindow.dispatchEvent(initEvent);
            }
            return true;
        });
    };
    ChartContent.prototype._getMarkers = function () {
        var _this = this;
        return function (markerType) {
            return _this._markersHandler.getMarkers(_this._idNotation, markerType);
        };
    };
    ChartContent.prototype._translate = function () {
        var _this = this;
        return function (key, params) {
            return _this._markersHandler.translate(key, params);
        };
    };
    ChartContent.prototype._persistLayout = function () {
        var _this = this;
        return function (chartLayout) {
            //for mobile we do not store any settings based on widget ids, no mini charts ever stored
            if (!isMobile() && _this.chartContentNature !== 'mini') {
                if (_this._isLayoutChanged(chartLayout)) {
                    _this._chartLayout = chartLayout;
                    _this._saveSettings(chartLayout);
                }
            }
        };
    };
    ChartContent.prototype._isLayoutChanged = function (chartLayout) {
        var _this = this;
        var changed = false;
        if (chartLayout) {
            if (this._chartLayout) {
                var changedProperties_1 = [];
                Object.keys(chartLayout).forEach(function (prop) {
                    if (typeof chartLayout[prop] === 'object') {
                        if (JSON.stringify(chartLayout[prop]) !== JSON.stringify(_this._chartLayout[prop])) {
                            changedProperties_1.push(prop);
                        }
                    }
                    else {
                        if (chartLayout[prop] !== _this._chartLayout[prop]) {
                            changedProperties_1.push(prop);
                        }
                    }
                });
                if (chartLayout.changingRange && !changedProperties_1.includes('setSpan')) {
                    changed = false;
                }
                else {
                    changed = changedProperties_1.length > 0;
                }
            }
            else {
                changed = true;
            }
        }
        return changed;
    };
    ChartContent.prototype._persistDrawings = function () {
        var _this = this;
        return function (serializedDrawings) {
            if (_this.chartContentNature !== 'mini') {
                if (serializedDrawings &&
                    JSON.stringify(serializedDrawings) !== JSON.stringify(_this._serializedDrawings)) {
                    _this._serializedDrawings = serializedDrawings;
                    _this._saveDrawings(serializedDrawings);
                }
            }
        };
    };
    ChartContent.prototype._removeChartInformation = function () {
        this._serializedDrawings = null;
    };
    return ChartContent;
}(AbstractChartContent));
export { ChartContent };
//# sourceMappingURL=chart-content.js.map